import { create } from 'zustand';
import type { WavePickList, RouteStep, PickList, PickItem } from '../types';

interface BulkPickingState {
  currentWave: WavePickList | null;
  availableWaves: WavePickList[];
  optimizedRoute: RouteStep[];
  collaborativePickers: string[];
  isLoading: boolean;
  error: string | null;
}

interface BulkPickingStore extends BulkPickingState {
  // Actions
  loadAvailableWaves: () => Promise<void>;
  createWave: (orders: PickList[], temperatureZone?: string) => Promise<WavePickList>;
  assignPickersToWave: (waveId: string, pickerIds: string[]) => Promise<void>;
  optimizeWaveRoute: (waveId: string) => Promise<RouteStep[]>;
  startWavePicking: (waveId: string) => Promise<void>;
  updateWaveProgress: (waveId: string, pickerId: string, itemId: string, quantity: number) => Promise<void>;
  completeWave: (waveId: string) => Promise<void>;
  setCurrentWave: (wave: WavePickList | null) => void;
  clearError: () => void;
}

export const useBulkPickingStore = create<BulkPickingStore>((set, get) => ({
  currentWave: null,
  availableWaves: [],
  optimizedRoute: [],
  collaborativePickers: [],
  isLoading: false,
  error: null,

  loadAvailableWaves: async () => {
    set({ isLoading: true, error: null });
    
    try {
      // For now, return empty array since we don't have wave tables yet
      // In a real implementation, this would query wave_pick_lists table
      const demoWaves: WavePickList[] = [];
      
      set({ availableWaves: demoWaves, isLoading: false });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to load waves',
        isLoading: false 
      });
    }
  },

  createWave: async (orders: PickList[], temperatureZone?: string) => {
    set({ isLoading: true, error: null });
    
    try {
      const totalItems = orders.reduce((sum, order) => sum + order.totalItems, 0);
      const estimatedTime = Math.ceil(totalItems * 1.5); // 1.5 minutes per item estimate
      
      const newWave: WavePickList = {
        id: `wave-${Date.now()}`,
        waveNumber: `WAVE-${String(Date.now()).slice(-3)}`,
        status: 'pending',
        orders,
        assignedPickers: [],
        totalItems,
        pickedItems: 0,
        estimatedTime,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        temperatureZone: temperatureZone as any
      };
      
      const state = get();
      set({ 
        availableWaves: [...state.availableWaves, newWave],
        isLoading: false 
      });
      
      return newWave;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to create wave',
        isLoading: false 
      });
      throw error;
    }
  },

  assignPickersToWave: async (waveId: string, pickerIds: string[]) => {
    set({ isLoading: true, error: null });
    
    try {
      const state = get();
      const updatedWaves = state.availableWaves.map(wave =>
        wave.id === waveId
          ? { ...wave, assignedPickers: pickerIds, updatedAt: new Date().toISOString() }
          : wave
      );
      
      set({ availableWaves: updatedWaves, isLoading: false });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to assign pickers',
        isLoading: false 
      });
    }
  },

  optimizeWaveRoute: async (waveId: string) => {
    set({ isLoading: true, error: null });
    
    try {
      // Demo route optimization
      const optimizedRoute: RouteStep[] = [
        {
          id: 'step-1',
          binLocation: 'A1-B2',
          items: [],
          order: 1,
          estimatedTime: 5,
          zone: 'Zone A'
        },
        {
          id: 'step-2',
          binLocation: 'A2-C1',
          items: [],
          order: 2,
          estimatedTime: 3,
          zone: 'Zone A'
        },
        {
          id: 'step-3',
          binLocation: 'B1-A3',
          items: [],
          order: 3,
          estimatedTime: 7,
          zone: 'Zone B'
        }
      ];
      
      set({ optimizedRoute, isLoading: false });
      return optimizedRoute;
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to optimize route',
        isLoading: false 
      });
      throw error;
    }
  },

  startWavePicking: async (waveId: string) => {
    set({ isLoading: true, error: null });
    
    try {
      const state = get();
      const wave = state.availableWaves.find(w => w.id === waveId);
      
      if (wave) {
        const updatedWave = { 
          ...wave, 
          status: 'in_progress' as const,
          updatedAt: new Date().toISOString() 
        };
        
        const updatedWaves = state.availableWaves.map(w =>
          w.id === waveId ? updatedWave : w
        );
        
        set({ 
          currentWave: updatedWave,
          availableWaves: updatedWaves,
          isLoading: false 
        });
      }
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to start wave',
        isLoading: false 
      });
    }
  },

  updateWaveProgress: async (waveId: string, pickerId: string, itemId: string, quantity: number) => {
    set({ isLoading: true, error: null });
    
    try {
      const state = get();
      const wave = state.availableWaves.find(w => w.id === waveId);
      
      if (wave) {
        const updatedWave = { 
          ...wave, 
          pickedItems: wave.pickedItems + quantity,
          updatedAt: new Date().toISOString() 
        };
        
        const updatedWaves = state.availableWaves.map(w =>
          w.id === waveId ? updatedWave : w
        );
        
        set({ 
          currentWave: state.currentWave?.id === waveId ? updatedWave : state.currentWave,
          availableWaves: updatedWaves,
          isLoading: false 
        });
      }
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to update progress',
        isLoading: false 
      });
    }
  },

  completeWave: async (waveId: string) => {
    set({ isLoading: true, error: null });
    
    try {
      const state = get();
      const updatedWaves = state.availableWaves.map(wave =>
        wave.id === waveId
          ? { ...wave, status: 'completed' as const, updatedAt: new Date().toISOString() }
          : wave
      );
      
      set({ 
        availableWaves: updatedWaves,
        currentWave: null,
        isLoading: false 
      });
    } catch (error) {
      set({ 
        error: error instanceof Error ? error.message : 'Failed to complete wave',
        isLoading: false 
      });
    }
  },

  setCurrentWave: (wave) => set({ currentWave: wave }),

  clearError: () => set({ error: null })
}));